// ===================================================================
// CENTRALIZED API CONFIGURATION
// This is the SINGLE source of truth for all API URLs
// ===================================================================

// Central URL determination function
export const determineApiBaseUrl = (): string => {
  const currentLocation = window.location.hostname;
  
  console.log('🌐 Determining API Base URL:');
  console.log(`   Current Hostname: ${currentLocation}`);
  console.log(`   Current Full Location: ${window.location.href}`);
  console.log(`   Explicit VITE_API_BASE_URL: ${import.meta.env.VITE_API_BASE_URL}`);

  // 1. Explicit environment variable takes highest priority
  const explicitUrl = import.meta.env.VITE_API_BASE_URL;
  if (explicitUrl) {
    console.log('✅ Using explicitly set VITE_API_BASE_URL:', explicitUrl);
    return explicitUrl;
  }

  // 2. Dynamic URL selection based on hostname
  if (currentLocation.includes('localhost') || currentLocation === '127.0.0.1') {
    console.log('🖥️ Localhost detected: Using local backend');
    return 'http://localhost:5001';
  }

  if (currentLocation.includes('staging')) {
    console.log('🚧 Staging environment detected');
    return 'https://backend-api-staging.fitted.fashion';
  }

  if (currentLocation.includes('fabrics.fitted.fashion')) {
    console.log('🌍 Production environment detected');
    return 'https://backend-api.fitted.fashion';
  }

  // 3. Fallback to staging
  console.warn('⚠️ No matching environment found. Using default staging URL.');
  return 'https://backend-api-staging.fitted.fashion';
};

// Export the determined base URL for all services
export const API_BASE_URL = determineApiBaseUrl();

interface ApiConfig {
  BASE_URL: string;
  TIMEOUT: number;
  RETRY_ATTEMPTS: number;
  RETRY_DELAY: number;
  ENDPOINTS: {
    AUTH_V2: string;
    AUTH_V1: string;
    FABRIC_V1: string;
    USER_V1: string;
  };
  HEADERS: {
    CONTENT_TYPE: string;
    ACCEPT: string;
  };
}

// API Configuration (uses centralized base URL)
export const API_CONFIG: ApiConfig = {
  // Use centralized base URL
  BASE_URL: API_BASE_URL,
  TIMEOUT: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000', 10),
  RETRY_ATTEMPTS: parseInt(import.meta.env.VITE_API_RETRY_ATTEMPTS || '3', 10),
  RETRY_DELAY: parseInt(import.meta.env.VITE_API_RETRY_DELAY || '1000', 10),

  // API Endpoints (matches backend routing)
  ENDPOINTS: {
    AUTH_V2: '/api/v2/auth-2',      // New auth system
    AUTH_V1: '/api/v1/auth',        // Legacy auth
    FABRIC_V1: '/api/v1/fabric',    // Fabric APIs
    USER_V1: '/api/v1/user',        // User APIs
  },

  // Standard headers
  HEADERS: {
    CONTENT_TYPE: 'application/json',
    ACCEPT: 'application/json',
  },
};

// Environment-specific configurations
export const ENV_CONFIG = {
  NODE_ENV: import.meta.env.MODE as 'development' | 'production' | 'staging',
  IS_DEVELOPMENT: import.meta.env.DEV,
  IS_PRODUCTION: import.meta.env.PROD,
  IS_STAGING: import.meta.env.MODE === 'staging',
  
  // Feature flags from environment variables
  FEATURES: {
    ERROR_DASHBOARD: import.meta.env.VITE_ENABLE_ERROR_DASHBOARD === 'true' || import.meta.env.DEV,
    DEBUG_LOGGING: import.meta.env.VITE_ENABLE_DEBUG_LOGGING === 'true' || import.meta.env.DEV,
    ANALYTICS: import.meta.env.VITE_ENABLE_ANALYTICS === 'true' || import.meta.env.PROD,
    ERROR_MONITORING: import.meta.env.VITE_ENABLE_ERROR_MONITORING === 'true' || import.meta.env.PROD,
  },

  // Platform configuration - FABRIC BUYER APP ONLY
  PLATFORM: {
    CURRENT: 'fabrics' as const, // This app ONLY serves fabric buyers
    SUPPORTED: ['fabrics'] as const, // Only fabric platform supported
  },

  // Auth configuration
  AUTH: {
    TOKEN_KEY: 'fitted_auth_token',
    REFRESH_TOKEN_KEY: 'fitted_refresh_token',
    TOKEN_EXPIRY_BUFFER: 5 * 60 * 1000, // 5 minutes before expiry
    AUTO_REFRESH: import.meta.env.VITE_AUTO_REFRESH_TOKEN !== 'false', // Default to true
  },
};

// Platform-specific URLs - FABRIC BUYER APP ONLY
export const PLATFORM_URLS = {
  fabrics: window.location.origin, // This app's URL (current domain)
  // Other apps are separate - we only link to them, don't serve them
};

// Validation helper
export const validateApiConfig = () => {
  try {
    console.log('🔧 API Configuration Validation:');
    console.log(`   Base URL: ${API_BASE_URL}`);
    console.log(`   Environment: ${ENV_CONFIG.NODE_ENV}`);
    console.log(`   Platform: ${ENV_CONFIG.PLATFORM.CURRENT}`);
    console.log(`   Features:`, ENV_CONFIG.FEATURES);
    
    // Test URL format
    const url = new URL(API_BASE_URL);
    console.log(`   Protocol: ${url.protocol}`);
    console.log(`   Hostname: ${url.hostname}`);
    console.log(`   Port: ${url.port || 'default'}`);
    
    return true;
  } catch (error) {
    console.error('❌ API Configuration Error:', error);
    return false;
  }
};

// Export for use in services
export default API_CONFIG; 
